/**
 * Utility for safely importing Node.js modules in a way that
 * won't break browser bundling.
 *
 * @param moduleName The name of the module to import
 * @returns A promise resolving to the imported module
 */
export async function safeNodeImport<T>(moduleName: string): Promise<T> {
  // More reliable environment detection
  const isBrowser =
    typeof window !== 'undefined' && typeof process === 'undefined';
  const isTestEnvironment = !isBrowser && process?.env?.NODE_ENV === 'test';

  // Only throw in actual browser environments, not in test
  if (isBrowser && !isTestEnvironment) {
    return Promise.reject(
      new Error(
        `Cannot import Node.js module '${moduleName}' in browser environment`
      )
    );
  }

  try {
    // Use dynamic import directly for better compatibility
    const module = await import(/* @vite-ignore */ moduleName);
    return module as T;
  } catch (error) {
    console.error(`Failed to dynamically import '${moduleName}':`, error);
    throw error;
  }
}
